import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Ζωτικότητα',
    description: 'Ανωνυμοποιημένες, ενδεικτικές τάσεις ευεξίας σε επίπεδο ομάδας.',
    noScope: 'Επιλέξτε μια εταιρεία για να δείτε τα στοιχεία ζωτικότητας.',
    noDepartments: 'Κανένα τμήμα εδώ δεν έχει στατιστικά που μπορείτε να δείτε.',
    disclaimer:
      'Όλα τα στοιχεία είναι <strong>ενδεικτικές πληροφορίες ευεξίας</strong> και <strong>δεν αποτελούν ιατρική διάγνωση</strong>. Οι τάσεις εμφανίζονται μόνο όταν υπάρχει επαρκής αριθμός συμμετεχόντων (ιδιωτικότητα εκ σχεδιασμού).',
  },
  filters: {
    title: 'Φίλτρα',
    department: {
      label: 'Τμήμα',
    },
    location: {
      label: 'Τοποθεσία',
      all: 'Όλες οι τοποθεσίες',
    },
  },
  badge: {
    measured: 'Μετρημένο',
    aiDerived: 'Ανάλυση AI',
  },
  level: {
    good: 'Καλό',
    opportunity: 'Ευκαιρία',
  },
  statusPill: {
    healthy: 'Υγιές επίπεδο',
    opportunity: 'Ευκαιρία βοήθειας',
    strongForward: 'Δυνατό & ανοδικό',
  },
  trend: {
    flat: 'Καμία αλλαγή',
    vsPrevious: 'σε σχέση με την προηγούμενη περίοδο',
    vsAverage: 'σε σχέση με τον μέσο όρο της περιόδου',
    up: 'Ανοδικό',
    down: 'Ελαφρώς χαμηλότερο',
    upBad: 'Ελαφρώς υψηλότερο',
    week: 'εβδ.',
  },
  units: {
    hoursMinutes: '{{h}}ω {{m}}λ',
    average: 'μ.ο. {{value}}',
  },
  status: {
    noData: 'Δεν υπάρχουν δεδομένα σε αυτή την περίοδο.',
  },
  table: {
    level: 'Επίπεδο',
    trend: 'Τάση',
    action: 'Ενέργεια',
    actionNone: '—',
    actionLink: 'Τμήμα',
    actionLinkTitle: 'Μετάβαση σε αυτό το τμήμα',
  },
  insight: {
    title: 'Τι δείχνουν τα στοιχεία ζωτικότητας',
    unavailable: 'Δεν υπάρχουν ακόμη αρκετά δεδομένα για σύνοψη.',
    vitality: {
      up: 'Η ζωτικότητα αυξάνεται ({{pct}}% {{period}}) με ποσοστό συμμετοχής {{participation}}%.',
      down: 'Η ζωτικότητα μειώνεται ({{pct}}% {{period}}) με ποσοστό συμμετοχής {{participation}}%.',
      flat: 'Η ζωτικότητα είναι σταθερή ({{pct}}% {{period}}) με ποσοστό συμμετοχής {{participation}}%.',
      noParticipation: 'Η ζωτικότητα είναι σταθερή ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'τις τελευταίες 7 ημέρες',
      last30Days: 'αυτόν τον μήνα',
      thisWeek: 'αυτή την εβδομάδα',
      thisMonth: 'αυτόν τον μήνα',
      custom: 'αυτήν την περίοδο',
    },
    stress: {
      up: 'Το άγχος αυξάνεται ({{pct}}%)',
      down: 'το άγχος μειώνεται ({{pct}}%)',
      flat: 'το άγχος παραμένει σταθερό',
    },
    sleep: {
      longer: 'και οι εργαζόμενοι κοιμούνται κατά μέσο όρο {{minutes}} λεπτά περισσότερο.',
      shorter: 'και οι εργαζόμενοι κοιμούνται κατά μέσο όρο {{minutes}} λεπτά λιγότερο.',
      same: 'και οι εργαζόμενοι κοιμούνται περίπου το ίδιο.',
    },
    mostActiveDay: 'Η {{day}} είναι η πιο δραστήρια ημέρα.',
  },
  cards: {
    vitalityScore: {
      title: 'Βαθμολογία ζωτικότητας',
      unit: '%',
      info: 'Μια σύνθετη βαθμολογία με βάση τον ύπνο, τη δραστηριότητα, το HRV, το άγχος και τον καρδιακό ρυθμό ηρεμίας.',
    },
    sleep: {
      title: 'Ύπνος',
      unit: '',
      info: 'Μέση διάρκεια ύπνου ανά νύχτα.',
    },
    stress: {
      title: 'Άγχος',
      unit: '%',
      info: 'Μετρημένο επίπεδο άγχους· όσο χαμηλότερο, τόσο πιο ήρεμο.',
    },
    recovery: {
      title: 'Αποκατάσταση',
      unit: '%',
      info: 'Πόσο καλά αναρρώνει το σώμα, με βάση τον ύπνο, το HRV και το άγχος.',
    },
    activity: {
      title: 'Δραστηριότητα',
      unit: 'βήματα/ημέρα',
      info: 'Μέσος αριθμός βημάτων ανά ημέρα.',
    },
    hrv: {
      title: 'HRV',
      unit: 'ms',
      info: 'Μεταβλητότητα καρδιακού ρυθμού — δείκτης αποκατάστασης και ανθεκτικότητας.',
    },
    restingHeartRate: {
      title: 'Καρδιακός ρυθμός ηρεμίας',
      unit: 'bpm',
      info: 'Ο χαμηλότερος μετρημένος καρδιακός ρυθμός στην επιλεγμένη περίοδο, ως προσέγγιση του καρδιακού ρυθμού ηρεμίας.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'Η ζωτικότητα εξελίσσεται καλά — διατηρήστε τις τρέχουσες συνήθειες.',
      opportunity:
        'Υπάρχει περιθώριο βελτίωσης στη ζωτικότητα — μια σύντομη συζήτηση με την ομάδα μπορεί να αναδείξει τι εμποδίζει.',
    },
    sleep: {
      good: 'Ο ύπνος της ομάδας φαίνεται υγιής — δεν χρειάζεται ενέργεια.',
      opportunity:
        'Ο ύπνος είναι κάτω από τον στόχο — σκεφτείτε μια υπενθύμιση για σταθερή βραδινή ρουτίνα και λιγότερη οθόνη πριν τον ύπνο.',
    },
    stress: {
      good: 'Το επίπεδο άγχους είναι υπό έλεγχο — συνεχίστε την παρακολούθηση.',
      opportunity:
        'Το άγχος είναι αυξημένο — μια σύντομη συζήτηση για τον φόρτο εργασίας ή μια δραστηριότητα αποκατάστασης μπορεί να βοηθήσει.',
    },
    recovery: {
      good: 'Η αποκατάσταση είναι σταθερή — η ομάδα ανακάμπτει καλά μετά την προσπάθεια.',
      opportunity:
        'Η αποκατάσταση υστερεί — ελέγξτε αν οι μέρες ανάπαυσης και ο φόρτος εργασίας είναι ισορροπημένα.',
    },
    activity: {
      good: 'Το επίπεδο δραστηριότητας είναι υγιές — δεν χρειάζεται ενέργεια.',
      opportunity:
        'Η δραστηριότητα είναι κάτω από τον στόχο — μια συνάντηση με περπάτημα ή μια πρόκληση βημάτων μπορεί να βοηθήσει.',
    },
    hrv: {
      good: 'Το HRV φαίνεται υγιές — δεν χρειάζεται ενέργεια.',
      opportunity:
        'Το HRV είναι χαμηλότερο από το αναμενόμενο — συχνά συνδέεται με άγχος ή κακό ύπνο· αξίζει συζήτηση μαζί.',
    },
    restingHeartRate: {
      good: 'Ο καρδιακός ρυθμός ηρεμίας βρίσκεται σε υγιές εύρος.',
      opportunity:
        'Ο καρδιακός ρυθμός ηρεμίας είναι αυξημένος — σε συνδυασμό με άγχος ή λιγότερο ύπνο, αξίζει να σημειωθεί.',
    },
  },
};
